import { CaregiverRecord, CaregiverRecordList } from "../messages/caregiver";
import { CaregiverId } from "../messages/ids";
import { DatabaseApiService } from "./db";

/**
 * How many ids go in one request.
 *
 * `ids` travels in the query string, so the list has to stay well inside the
 * shortest URL limit anything in the path might impose. A caregiver id is about
 * eight characters with its comma, so 300 keeps a request near 2.5KB however
 * many caregivers a page is asking about.
 */
const CHUNK_SIZE = 300;

/**
 * Resolves caregivers as full records, by id.
 *
 * The counterpart of `caregiverLookupService`, which answers from the search
 * index and so carries only what the index holds - no birth date, gender, install
 * history or hire date. Pages that show those columns need the database row, and
 * this is how they ask for exactly the caregivers their own rows reference
 * instead of the whole agency.
 *
 * Two things it deliberately does that a bare `DatabaseApi.get` does not: it
 * chunks, so a page whose date range widened to thousands of caregivers does not
 * build a URL nothing will accept; and it caches, so paging or re-filtering does
 * not re-ask for caregivers it already has.
 */
//! @ngInject
export class CaregiverRecordsService {
  private cache: Map<string, CaregiverRecord> = new Map();

  constructor(
    private DatabaseApi: DatabaseApiService,
    private $q: ng.IQService,
    private $rootScope: ng.IRootScopeService
  ) {}

  /**
   * The records for `ids`, keyed by id as a string.
   *
   * Ids that match no caregiver in this agency are simply absent from the map -
   * callers decide what an unresolvable row means for them.
   */
  byIds = (
    ids: (CaregiverId | number)[],
    options: { withPhoneNumbers?: boolean } = {}
  ): ng.IPromise<Map<string, CaregiverRecord>> => {
    const wanted = [
      ...new Set(ids.filter((id) => id !== null && id !== undefined).map((id) => String(id))),
    ];
    const missing = wanted.filter((id) => !this.cache.has(id));

    if (missing.length === 0) {
      return this.$q.resolve(this.pick(wanted));
    }

    const chunks: string[][] = [];
    for (let i = 0; i < missing.length; i += CHUNK_SIZE) {
      chunks.push(missing.slice(i, i + CHUNK_SIZE));
    }

    return this.$q
      .all(chunks.map((chunk) => this.fetchChunk(chunk, options.withPhoneNumbers === true)))
      .then(() => this.pick(wanted));
  };

  /**
   * Drops everything cached.
   *
   * A caregiver's row can change under us - a status edit, a new certification -
   * and nothing here is invalidated by a socket event, so a page that needs to be
   * sure it is looking at current data clears the cache first.
   */
  clear = () => {
    this.cache = new Map();
  };

  private fetchChunk = (ids: string[], withPhoneNumbers: boolean) => {
    const query = [
      `ids=${ids.join(",")}`,
      ...(withPhoneNumbers ? ["withPhoneNumbers=true"] : []),
    ].join("&");

    return this.DatabaseApi.get(
      `agencies/${this.$rootScope.agencyId}/agency_members/${this.$rootScope.agencyMemberId}/caregivers/records?${query}`
    ).then((res: { data: CaregiverRecordList }) => {
      for (const caregiver of res.data.caregivers) {
        this.cache.set(String(caregiver.id), caregiver);
      }
    });
  };

  private pick = (ids: string[]): Map<string, CaregiverRecord> => {
    const found: Map<string, CaregiverRecord> = new Map();

    for (const id of ids) {
      const caregiver = this.cache.get(id);

      if (caregiver !== undefined) {
        found.set(id, caregiver);
      }
    }

    return found;
  };
}
